import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { AppConfiguration, CurrentSettings } from 'src/app/interfaces/biozoom-api/app-configuration';
import { PreRequestResult } from 'src/app/interfaces/biozoom-api/pre-request-result';
import { environment } from '../../../environments/environment';
import { UtilsService } from './utils.service';
import { ConnectionService } from './connection.service';
import { HttpEvent } from '@angular/common/http';
import { AoxMeasurementResult } from 'src/app/interfaces/biozoom-api/aox-measurement-result';
import { ApiProgressUtilsService } from './api-progress-utils.service';
import { PreciseAoxMeasurementResult } from 'src/app/interfaces/biozoom-api/precise-aox-measurement-result';
import { HrvMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-measurement-result';
import { HrvTestMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-test-measurement-result';
import { DemoUtilsService } from './demo-utils.service';
import { ScannerStatus } from 'src/app/interfaces/scanner-api/scanner-status';
import { WifiNameResult } from 'src/app/interfaces/biozoom-api/wifi-name-result';
import { Platform } from '@ionic/angular';
import {getApiUrl} from '../../methods/helpers';

@Injectable({
  providedIn: 'root'
})
/**
 * api service for low-level methods
 * these perform a single service, only once if it's an async service, and do not handle errors/retries, nor format the response
 */
export class ApiBasicService {
  apiCount = 0;

  constructor(
    private progressUtilsService: ApiProgressUtilsService,
    private demoUtilService: DemoUtilsService,
    private utilsService: UtilsService,
    private connectionService: ConnectionService,
    private platform: Platform,
  ) { }

  getOS = () => {
    let os = '';
    if (this.platform.is('android')) {
      os = 'android';
    } else if (this.platform.is('ios')) {
      os = 'ios';
    }
    return os;
  }

  /**
   * load the application's config file from the environment, and reformat some of the data
   * @param language user language
   * @returns loaded and updated config file
   */
  getConfiguration(language = 'de'): AppConfiguration {
    const twoLetterLanguage = (language.substring(0, 2).toLowerCase());
    const config: any = {
      firmware_10_version: undefined, firmware_10_filename: undefined,
      firmware_20_version: undefined, firmware_20_filename: undefined,
      firmware_nuvoton_version: undefined, firmware_nuvoton_filename: undefined,
      ...environment.config
    };
    // config.dataPrivacyUrl = config.dataPrivacyUrl.replace(/XX/g, twoLetterLanguage);
    // config.instructionLink = config.instructionLink + twoLetterLanguage;
    config.validProducts = environment.validProducts;
    config.firmware_10_version = '10.4.2';
    config.firmware_10_filename = 'scanner_firmware-10-4-2.bin';
    config.firmware_20_version = '20.4.2';
    config.firmware_20_filename = 'scanner_firmware-20-4-2.bin';
    config.firmware_hw1_version = '1.8.14';
    config.firmware_hw1_filename = 'firmware_1_8_14.bin';
    config.firmware_hw2_version = '2.1.21';
    config.firmware_hw2_filename = 'firmware_2_1_21.bin';
    config.firmware_hw3_version = '3.1.21';
    config.firmware_hw3_filename = 'firmware_3_1_21.bin';
    config.firmware_hw4_version = '4.1.21';
    config.firmware_hw4_filename = 'firmware_4_1_21.bin';
    config.firmware_hw2_version_old = '2.1.14';
    config.firmware_hw2_filename_old = 'firmware_2_1_14.bin';
    config.firmware_hw3_version_old = '3.1.14';
    config.firmware_hw3_filename_old = 'firmware_3_1_14.bin';
    config.firmware_hw4_version_old = '4.1.15';
    config.firmware_hw4_filename_old = 'firmware_4_1_15.bin';
    config.firmware_nuvoton_version = '0.5.4';
    config.firmware_nuvoton_filename = 'nuvoton_firmware-0-5-4.hex';
    config.sensor_version = 604;
    config.sensor_filename = 'infineon-0-6-4.hex';
    if (environment.appDesignSettings?.calcURLs) {
      const urls: string[] = (environment.appDesignSettings.calcURLs ?? '').replace(/ /g, '').split(',');
      if (urls.length > 0) {
        config.vwsBaseUrls = urls;
      }
    }
    return config;
  }


  getSettings(): CurrentSettings {
    const settings = { ...environment.settings };
    Object.entries(environment.appDesignSettings?.appParameters ?? {}).forEach(([key, val]) => {
      settings[key] = isNaN(Number(val)) ? val : Number(val);
    });
    return settings;
  }

  /**
   * perform a single pre-request to the specified VWS
   * the response contains information about which VWS top send the actual measurement to
   * this is used for load-balancing reasons
   * @param config loaded config file
   * @param vwsBaseUrl base url of the VWS that is to be contacted
   * @param serial serial ID of the scanner
   * @param measuretype 0->AOX, 1->AOX_MULTI, 2->HRV, 3->HRV-TEST
   * @param demoMode if true, perform a mock-request instead of actually contacting the server
   * @returns observable for the server response
   */
  doPrerequestSingle = (
    config: AppConfiguration,
    vwsBaseUrl: string,
    serial: string,
    measuretype: number,
    demoMode = false,
    preRequestTimeoutMs?: number,
  ): Observable<PreRequestResult> => {
    let request: Observable<PreRequestResult>;
    if (demoMode) {
      const newPreRequest = Math.random() > 0.5;
      const result: PreRequestResult = {
        new_pre_request_delay_ms: newPreRequest ? Math.random() * 1000 : 0,
        new_pre_request_req: newPreRequest,
        recommended_server: 'https://vws1.biozoom.org',
        waiting_time_ms: 1000
      };

      request = of<PreRequestResult>(result).pipe(delay(500));
    } else {
      if (!vwsBaseUrl) {
        return this.utilsService.raiseError('No VWS url provided.');
      }

      const url = vwsBaseUrl + config.vwsPrerequestUrl + '?serial_id=' + serial + '&measure_type=' + measuretype;
      request = this.connectionService.getPrerequest(url);
    }
    return this.connectionService.addTimeoutToRequest(
      request,
      this.getVwsPreRequestTimeout(preRequestTimeoutMs)
    );
  }

  private getVwsPreRequestTimeout = (timeoutMs?: number): number => {
    const timeout = Number(timeoutMs);
    return Number.isFinite(timeout) && timeout > 0 ? timeout : 10000;
  }

  /**
   * perform a single request to the server, uploading a quick aox measurement result, and retrieving the calculated response
   * use a VWS
   * @param serial serial ID of the scanner
   * @param data data of a single aox measurement
   * @param delayMs delay the response by x milliseconds
   * @param recServer ID of the VWS server that is to be used
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  doMeasurementVWSSingle = (
    serial: string,
    data: string,
    delayMs: number,
    recServer: string,
    demoMode = false,
    language = 'de',
    additionalData?: any
  ): Observable<HttpEvent<AoxMeasurementResult>> => {

    if (demoMode) {
      const result: AoxMeasurementResult = this.demoUtilService.genMeasurementAoxRequestDemo(additionalData, serial);

      return this.progressUtilsService.generateUploadProgress<AoxMeasurementResult>(1500, result, delayMs);
    }
    const { form } = this.utilsService.genMeasurementRequestMetadata(
      serial, true, false, false, false, language, data, additionalData
    );

    return this.connectionService.getMeasurementAoxVWSRequest(
      recServer, this.getConfiguration().vwsMeasurementUrl, serial, form
    );
  }

  /**
   * perform a single request to the server, uploading a quick aox measurement result, and retrieving the calculated response
   * do not use a VWS
   * @param serial serial ID of the scanner
   * @param data data of a single aox measurement
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  doMeasurementSingle = (
    serial: string,
    data: string,
    demoMode = false,
    language = 'de',
    additionalData?: any
  ) => {
    if (demoMode) {
      const result: AoxMeasurementResult = this.demoUtilService.genMeasurementAoxRequestDemo(additionalData, serial);
      return this.progressUtilsService.generateUploadProgress<AoxMeasurementResult>(5000, result, 1000);
    }

    const { headers } = this.utilsService.genMeasurementRequestMetadata(
      serial, false, false, false, false, language, data, additionalData
    );
    const body = this.utilsService.genMeasurementRequestBody(serial, language, data, additionalData);

    return this.connectionService.getMeasurementAoxRequest(this.getConfiguration().measurementUrl, body, headers);
  }

  /**
   * perform a single request to the server, uploading all gathered aox results, and retrieving the combined result
   * @param serial serial ID of the scanner
   * @param delayMs delay the response by x milliseconds
   * @param recServer ID of the VWS server that is to be used
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  doMeasurementPreciseVWSSingle = (
    serial: string,
    delayMs: number,
    recServer: string,
    demoMode = false,
    language = 'de',
    additionalData: {
      series_q: number[],
      series_results: number[]
    }

  ) => {
    if (demoMode) {
      const result: PreciseAoxMeasurementResult = this.demoUtilService.genMeasurementAoxPreciseRequestDemo();
      return this.progressUtilsService.generateUploadProgress(3744, result, delayMs);
    }
    const { form } = this.utilsService.genMeasurementRequestMetadata(
      serial, true, true, false, false, language, '', additionalData
    );

    return this.connectionService.getMeasurementAoxPreciseVWSRequest(
      recServer, this.getConfiguration().vwsMeasurementUrl, serial, form
    );
  }

  /**
   * perform a single request to the server, uploading the hrv measurement, and receiving the calculated result
   * @param serial serial ID of the scanner
   * @param data data of a single aox measurement
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @param measurementType info about the currently running app path/measurement type
   * @returns observable for the server response
   */
  doMeasurementHrvSingle = (
    serial: string,
    data: string,
    demoMode = false,
    language = 'de',
    additionalData?: any,
    measurementType?: string,
  ) => {
    if (demoMode) {
      let result: HrvMeasurementResult;
      if (measurementType === 'hrv'){
        result = this.demoUtilService.genMeasurementHrvRequestDemoWithoutAoxValue();
      }
      else {
        result = this.demoUtilService.genMeasurementHrvRequestDemo();
      }
      return this.progressUtilsService.generateUploadProgress<HrvMeasurementResult>(5000, result, 4525);
    }
    const { headers } = this.utilsService.genMeasurementRequestMetadata(
      serial, false, false, true, false, language, data, additionalData
    );
    const body = this.utilsService.genMeasurementRequestBody(serial, language, data, additionalData);

    return this.connectionService.getMeasurementHrvRequest(this.getConfiguration().measurementHrvUrl, body, headers);
  }

  /**
   * perform a single request to the server, uploading the test hrv measurement, and receiving the calculated result
   * @param serial serial ID of the scanner
   * @param data data of a single aox measurement
   * @param demoMode if true, return a mock-observable instead of performing an actual request
   * @param language currently used language
   * @param additionalData any additional data the backend might require
   * @returns observable for the server response
   */
  doMeasurementHrvTestSingle = (
    serial: string,
    data: string,
    demoMode = false,
    language = 'de',
    additionalData?: any
  ) => {
    if (demoMode) {
      const result: HrvTestMeasurementResult = this.demoUtilService.genMeasurementHrvTestRequestDemo();
      return of<HrvTestMeasurementResult>(result).pipe(delay(5000));
    }
    const { headers } = this.utilsService.genMeasurementRequestMetadata(
      serial, false, false, false, true, language, data, additionalData
    );
    const body = this.utilsService.genMeasurementRequestBody(serial, language, data, additionalData);

    return this.connectionService.getMeasurementHrvTestRequest(this.getConfiguration().measurementHrvUrl, body, headers);
  }

  /**
   * fetch the current version of the scanner
   * currently unused?
   * @returns observable for the server response
   */
  getVersion() {
    const config = this.getConfiguration();
    return this.connectionService.fetchVersion(config.testConnectionUrl, config.product);
  }

  /**
   * fetch the name of the Wi-Fi for the currently used scanner
   * @param serial serial ID of the scanner
   * @returns observable for the server response
   */
  getWiFiName = (serial: string): Observable<WifiNameResult> => {
    const config = this.getConfiguration();
    const url = getApiUrl(this.apiCount > 0);
    this.apiCount += 1;
    return this.connectionService.fetchWiFiName(
      url,
      config.updateUser,
      config.updatePassword,
      serial
    );
  }

  /**
   * check whether the product in the given ScannerStatus is in the list of valid products.
   * @param scannerStatus current scanner status
   * @return true, if the product is amongst the valid products
   */
  checkForValidProduct = (scannerStatus: ScannerStatus | {}): boolean => {
    let product = scannerStatus?.['product'] ?? {};
    if (typeof product === 'object') {
      product = product?.['product'] ?? '';
    }
    return this.getConfiguration().validProducts.includes(product);
  }

}
